import type { FC } from 'react';

import { Placeholder, useRendering, useSetting } from '@eneco/cms';
import type { ColumnAlignment, ColumnArrangement } from '@eneco/components/SplitView/SplitView';
import { SplitView as SplitViewComponent } from '@eneco/components/SplitView/SplitView';
import { Grid, Stack, Stretch } from '@eneco/sparky';
import type { BoxProps } from '@eneco/sparky/types';

import { resolveSlot, SPLIT_VIEW_SLOTS } from './slots';

/**
 * `SplitView` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Layout is the shared `@eneco/components/SplitView/SplitView` primitive, the same one the legacy
 * Sitecore lane uses (`libs/sitecore/components/content/src/SplitView/SplitView.tsx`) — no bespoke
 * markup here, just CMS wiring. `promoBox` is a single overlay shared by both columns (never rendered
 * twice), grid-stacked on top of whichever side has an `Image` child — same heuristic as the legacy
 * component.
 *
 * Known simplification: the legacy component's `pageEditing` branch (a stacked layout for the Sitecore
 * Experience Editor, with `jss-promo-box` rendered as a visible sibling rather than overlaid) is
 * dropped — this lane never runs inside the Experience Editor, on either provider.
 */
const SplitView: FC = () => {
  const rendering = useRendering();

  const backgroundFirst = useSetting('backgroundFirst') as BoxProps['backgroundColor'];
  const backgroundSecond = useSetting('backgroundSecond') as BoxProps['backgroundColor'];
  const bringToFront = useSetting('bringToFront');
  const columnArrangement = useSetting('columnArrangement') as ColumnArrangement | undefined;
  const columnAlignment = useSetting('columnAlignment') as ColumnAlignment | undefined;

  const firstSlot = resolveSlot(rendering, SPLIT_VIEW_SLOTS.first);
  const secondSlot = resolveSlot(rendering, SPLIT_VIEW_SLOTS.second);
  const promoSlot = resolveSlot(rendering, SPLIT_VIEW_SLOTS.promo);

  const firstHasImage = (firstSlot && rendering?.placeholders?.[firstSlot]?.some(r => r.componentName === 'Image')) ?? false;
  const secondHasImage = (secondSlot && rendering?.placeholders?.[secondSlot]?.some(r => r.componentName === 'Image')) ?? false;
  // Neither side has an image: the legacy component never overlays promoBox in that case either
  // (`firstHasImage`/`secondHasImage` both false skips the `SplitViewSlot` overlay branch entirely).
  const promoOnFirst = firstHasImage;
  const promoOnSecond = !firstHasImage && secondHasImage;

  return (
    <SplitViewComponent columnArrangement={columnArrangement} columnAlignment={columnAlignment}>
      <SplitViewComponent.Item backgroundColor={backgroundFirst} bringToFront={bringToFront === 'first'}>
        <Stretch height>
          <Stack gap={6}>
            {firstSlot ? <SplitViewSlot slot={firstSlot} promoSlot={promoOnFirst ? promoSlot : undefined} /> : null}
          </Stack>
        </Stretch>
      </SplitViewComponent.Item>
      <SplitViewComponent.Item backgroundColor={backgroundSecond} bringToFront={bringToFront === 'second'}>
        <Stretch height>
          <Stack gap={6}>
            {secondSlot ? <SplitViewSlot slot={secondSlot} promoSlot={promoOnSecond ? promoSlot : undefined} /> : null}
          </Stack>
        </Stretch>
      </SplitViewComponent.Item>
    </SplitViewComponent>
  );
};

/** Overlays `promoSlot` on top of `slot`'s content, or renders `slot` alone when there is no overlay. */
const SplitViewSlot: FC<{ slot: string; promoSlot?: string }> = ({ slot, promoSlot }) => {
  if (!promoSlot) return <Placeholder name={slot} />;

  return (
    <Grid gridTemplateColumns="40px auto 1fr" gridTemplateRows="20px auto 1fr">
      <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
        <Placeholder name={slot} />
      </Grid.Item>
      <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
        <Placeholder name={promoSlot} />
      </Grid.Item>
    </Grid>
  );
};

export default SplitView;
